import { useEffect, useState, useMemo } from "react";
import {
  Typography, Grid, Button, Box, TextField, FormControl, Chip,
  MenuItem, Select, Divider, FormHelperText, IconButton, Autocomplete,
  CircularProgress, Snackbar, Alert, Paper, InputAdornment
} from "@mui/material";
import { alpha, useTheme } from '@mui/material/styles';
import DeleteIcon from "@mui/icons-material/Delete";
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LaptopIcon from '@mui/icons-material/Laptop';
import HomeIcon from '@mui/icons-material/Home';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import { useSearchParams } from 'react-router-dom';
import PropTypes from "prop-types";
import GooglePlacesAutocomplete from 'react-google-places-autocomplete';

import FileUploader from "../../upload/FileUpload";
import { getCategories, getSubcategories } from "../../../actions/categories";
import { isEmpty, areAllValuesEmpty, urlImg } from "../../common/helpers";
import { addProfileService, updateProfileService, deleteServiceImage } from "../../../actions/profile";
import random from "../../common/random";
import typeImg from "../../common/typeImg";
import SelectorModule from "../../common/SelectorModule";
import DisponibilidadSemanal from "./DisponibilidadSemanal";
import { slotInvalido } from "./disponibilidad";
import AvisoCobro from "../../common/AvisoCobro";

import { useDispatch, useSelector } from "react-redux";
import { dinero } from "../../../utils/dinero";
import { MINIMO_COBRO } from "../../../utils/reglasPago";

const FormServicio = ({ irA }) => {
  // El buscador de direcciones de Google no es de MUI: su borde de error lleva el valor del tema (el mismo rojo que los demás campos)
  const { palette } = useTheme();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("service") || null;
  const servicios = useSelector(state => state.profile.profile.servicios);
  const categories = useSelector(state => state.cat.categories);
  const infoServicio = {
    nombre: "",
    descripcion: "",
    // Al crear un servicio desde una página llega la categoría de la página (?categoria=) para proponerla
    categoria: searchParams.get("categoria") || "",
    subcategoria: "",
    incluye: "",
    infoAdicional: "",
    ubicacion: {
      direccion: "",
      place_id: ""
    },
    tipoCosto: "",
    costoServicio: "",
    modalidades: [],
    idiomas: [],
    tags: [],
    duracionEstimada: "",
    unidadDuracion: "Horas",
    disponibilidad: [] // Structure: { day: 'Lunes', slots: [{inicio: '', fin: ''}] }
  }

  const [service, setService] = useState(infoServicio);
  const [errores, setErrores] = useState({});
  const [images, setImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [subcategorias, setSubCat] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: "", severity: "info" });

  useEffect(() => {
    dispatch(getCategories());
  }, []);

  useEffect(() => {
    if (id) {
      const filtrado = servicios.filter(s => s._id === id);
      setService({
        nombre: filtrado[0]?.nombre || "",
        descripcion: filtrado[0]?.descripcion || "",
        incluye: filtrado[0]?.incluye || "",
        categoria: filtrado[0]?.categoria || "",
        subcategoria: filtrado[0]?.subcategoria || "",
        // Nombres resueltos por el backend: permiten seguir mostrando la categoría/subcategoría
        // actual aunque haya sido deshabilitada.
        categoriaNombre: filtrado[0]?.categoriaNombre || "",
        subcategoriaNombre: filtrado[0]?.subcategoriaNombre || "",
        infoAdicional: filtrado[0]?.infoAdicional || "",
        ubicacion: filtrado[0]?.ubicacion || { direccion: "", place_id: "" },
        tipoCosto: filtrado[0]?.tipoCosto || "",
        costoServicio: filtrado[0]?.costoServicio || "",
        modalidades: filtrado[0]?.modalidades || [],
        idiomas: filtrado[0]?.idiomas || [],
        tags: filtrado[0]?.tags || [],
        duracionEstimada: filtrado[0]?.duracionEstimada || "",
        unidadDuracion: filtrado[0]?.unidadDuracion || "Horas",
        disponibilidad: filtrado[0]?.disponibilidad || []
      });
      if (filtrado[0]?.img) {
        setExistingImages(filtrado[0].img);
      }
    }
  }, [id, servicios]);

  useEffect(() => {
    if (service.categoria) {
      subCat(service.categoria);
    }
  }, [service.categoria]);

  const subCat = async cat => {
    const res = await getSubcategories(cat);
    setSubCat(res);
  }

  // Opciones de categoría: solo activas para elegir. Si se edita un servicio cuya categoría
  // fue deshabilitada, se agrega esa categoría actual para que siga mostrándose (sin poder
  // seleccionar otras deshabilitadas).
  const categoriaOptions = useMemo(() => {
    const activas = categories || [];
    if (service.categoria && !activas.some(c => c._id === service.categoria)) {
      return [...activas, { _id: service.categoria, name: service.categoriaNombre || 'Categoría actual (deshabilitada)' }];
    }
    return activas;
  }, [categories, service.categoria, service.categoriaNombre]);

  const subcategoriaOptions = useMemo(() => {
    const activas = subcategorias || [];
    if (service.subcategoria && !activas.some(s => s._id === service.subcategoria)) {
      return [...activas, { _id: service.subcategoria, name: service.subcategoriaNombre || 'Subcategoría actual (deshabilitada)' }];
    }
    return activas;
  }, [subcategorias, service.subcategoria, service.subcategoriaNombre]);

  const handleChange = (field, value) => {
    setService(prev => ({
      ...prev,
      [field]: value
    }));
  }

  const handleDeleteImage = async (imgName) => {
    const success = await dispatch(deleteServiceImage(id, imgName));
    if (success) {
      setExistingImages(prev => prev.filter(img => img.nombre !== imgName));
    }
  }

  const handleAddTag = () => {
    if (tagInput.trim() !== "" && !service.tags.includes(tagInput.trim())) {
      setService(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput("");
    }
  }

  const handleRemoveTag = (tagToDelete) => {
    setService(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToDelete)
    }));
  }

  const handleSubmit = async (e, borrador = false) => {
    e.preventDefault();
    if (loading) return;

    // Check if at least one meaningful field is filled for a draft
    const draftFields = [
      'nombre', 'descripcion', 'incluye', 'infoAdicional', 'costoServicio',
      'categoria', 'ubicacion', 'modalidades', 'idiomas', 'disponibilidad'
    ];

    const isAtLeastOneFieldFilled = draftFields.some(field => {
      const val = service[field];
      if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
        return !areAllValuesEmpty(val);
      }
      return !isEmpty(val);
    }) || images.length > 0 || existingImages.length > 0;

    if (borrador && !isAtLeastOneFieldFilled) {
      return setAlert({
        open: true,
        message: "Completa al menos un campo para guardar el borrador",
        severity: "warning"
      });
    }

    const info = {
      nombre: service.nombre,
      descripcion: service.descripcion,
      incluye: service.incluye,
      infoAdicional: service.infoAdicional,
      categoria: service.categoria || null,
      subcategoria: service.subcategoria || null,
      costoServicio: service.costoServicio,
      tipoCosto: service.tipoCosto,
      ubicacion: service.ubicacion || null,
      modalidades: service.modalidades,
      idiomas: service.idiomas,
      duracionEstimada: service.duracionEstimada,
      unidadDuracion: service.unidadDuracion,
      disponibilidad: service.disponibilidad,
      tags: service.tags || [],
      borrador
    };

    if (!borrador) {
      let erroresEncontrados = {};

      // Mandatory fields according to UI indicators (*)
      const requiredFields = [
        'nombre', 'descripcion', 'incluye', 'categoria',
        'costoServicio', 'tipoCosto', 'modalidades', 'disponibilidad', 'ubicacion'
      ];

      requiredFields.forEach(field => {
        if (field === 'ubicacion') {
          if (info.modalidades.includes('Presencial') && isEmpty(info.ubicacion.direccion)) {
            erroresEncontrados.ubicacion = "Debes ingresar una ubicación";
          }
        } else if (field === 'disponibilidad') {
          if (isEmpty(info[field])) {
            erroresEncontrados[field] = "Este es un campo obligatorio";
          }
        } else if (isEmpty(info[field])) {
          erroresEncontrados[field] = "Este es un campo obligatorio";
        }
      });

      // Ningún servicio es gratis: todo se paga en la plataforma, y lo mínimo que se puede cobrar son $10
      if (!erroresEncontrados.costoServicio && !(parseFloat(info.costoServicio) >= MINIMO_COBRO)) {
        erroresEncontrados.costoServicio = `Pon un precio de al menos ${dinero(MINIMO_COBRO)}: es lo mínimo que se puede cobrar.`;
      }

      // Cada horario debe tener inicio y fin, y el fin debe ser posterior al inicio (máximo 12:00 am)
      if (!erroresEncontrados.disponibilidad && !isEmpty(info.disponibilidad)) {
        const horarioInvalido = info.disponibilidad.some(d =>
          !(d.slots || []).length || d.slots.some(s => !s.inicio || !s.fin || slotInvalido(s))
        );
        if (horarioInvalido) {
          erroresEncontrados.disponibilidad = "Revisa los horarios: completa inicio y fin, y que el fin sea posterior al inicio (máximo 12:00 am).";
        }
      }

      if (images.length + existingImages.length < 3) {
        erroresEncontrados.imagenes = "Debes tener al menos 3 imágenes para tu servicio";
      }

      if (!isEmpty(erroresEncontrados)) {
        setErrores(erroresEncontrados);
        setAlert({
          open: true,
          message: "Por favor, completa los campos obligatorios",
          severity: "error"
        });
        return;
      }
    } else {
      // Clear errors if saving as draft
      setErrores({});
    }

    setLoading(true);

    const imgFormData = new FormData();
    const nombresImg = [];

    if (images.length > 0) {
      images.forEach((img, i) => {
        const identificador = random(7);
        const nombre = identificador + typeImg(img);

        // Add to JSON for DB
        nombresImg.push({ nombre });

        // Add to FormData for Upload
        imgFormData.append('files', img);
        imgFormData.append('nombre', identificador);
      });
    }

    // Add img array to service info
    info.img = nombresImg;

    if (!id) {
      const res = await dispatch(addProfileService(info, imgFormData));
      if (res) {
        setLoading(false);
        irA("/user/servicios");
      } else {
        setLoading(false);
      }
    } else {
      const res = await dispatch(updateProfileService(info, id, imgFormData));
      if (res) {
        setLoading(false);
        irA("/user/servicios");
      } else {
        setLoading(false);
      }
    }
  }

  return (
    <Box>
      <Typography variant="h6" color="primary">
        Crear Servicio Independiente
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ marginBottom: 3 }}
      >
        Completa la información de tu servicio para publicarlo
      </Typography>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={12}>
            <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Grid>
                <Typography variant="body1" color="primary" fontWeight={600}>
                  Información Básica
                </Typography>
              </Grid>
            </Grid>
            <Divider sx={{ mb: 3 }} />

            <Typography variant="body2" color="primary" fontWeight={600} sx={{ mb: 1 }}>
              Nombre del Servicio<Box component="span" sx={{ color: 'error.oscuro' }}>*</Box>
            </Typography>
            <TextField
              id="service"
              placeholder="Ej: Clase online de repostería casera"
              name="servicio"
              fullWidth
              value={service.nombre}
              onChange={(e) => handleChange("nombre", e.target.value)}
              error={!isEmpty(errores.nombre)}
              helperText={errores?.nombre}
            />
          </Grid>
          <Grid size={12}>
            <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Grid>
                <Typography variant="body2" color="primary" fontWeight={600}>
                  Descripción<Box component="span" sx={{ color: 'error.oscuro' }}>*</Box>
                </Typography>
              </Grid>
              <Grid>
                <Typography variant="caption" color="text.secondary">
                  {service.descripcion.length}/250
                </Typography>
              </Grid>
            </Grid>
            <TextField
              placeholder="Describe brevemente el servicio"
              multiline
              rows={4}
              variant="outlined"
              fullWidth
              autoComplete="off"
              value={service.descripcion}
              onChange={(e) => handleChange("descripcion", e.target.value.slice(0, 250))}
              error={!isEmpty(errores.descripcion)}
              helperText={errores?.descripcion}
            />
          </Grid>
          <Grid size={12}>
            <Typography variant="body2" color="primary" fontWeight={600} sx={{ mb: 1 }}>
              Lo que incluye<Box component="span" sx={{ color: 'error.oscuro' }}>*</Box>
            </Typography>
            <TextField
              placeholder="Ej: Material de trabajo, certificado, seguimiento post-servicio"
              multiline
              rows={4}
              variant="outlined"
              fullWidth
              autoComplete="off"
              value={service.incluye}
              onChange={(e) => handleChange("incluye", e.target.value)}
              error={!isEmpty(errores.incluye)}
              helperText={errores?.incluye}
            />
          </Grid>
          <Grid size={12}>
            <Typography variant="body2" color="primary" fontWeight={600} sx={{ mb: 1 }}>
              Información Adicional Relevante
            </Typography>
            <TextField
              name="info"
              placeholder="Requisitos previos, preparación necesaria, etc."
              multiline
              rows={4}
              variant="outlined"
              fullWidth
              autoComplete="off"
              value={service.infoAdicional}
              onChange={(e) => handleChange("infoAdicional", e.target.value)}
              error={!isEmpty(errores.infoAdicional)}
              helperText={errores?.infoAdicional}
            />
          </Grid>

          <Grid size={12} mb={2}>
            <Grid container justifyContent="space-between" alignItems="center" sx={{ mt: 2, mb: 1 }}>
              <Grid>
                <Typography variant="body1" color="primary" fontWeight={600}>
                  Fotos del Servicio
                </Typography>
              </Grid>
            </Grid>
            <Divider sx={{ mb: 3 }} />

            <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Grid>
                <Typography variant="body2" color="primary" fontWeight={600}>
                  Fotos del Servicio<Box component="span" sx={{ color: 'error.oscuro' }}>*</Box>
                </Typography>
              </Grid>
              <Grid>
                <Typography variant="caption">
                  {(images.length + existingImages.length)}/10 (3 minimo)
                </Typography>
              </Grid>
            </Grid>

            <FileUploader
              files={images}
              setFiles={setImages}
              multiple
            />
            <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
              Por favor sube al menos 3 imágenes
            </Typography>
            {
              !isEmpty(errores.imagenes) && (
                <FormHelperText error sx={{ textAlign: "center" }}>
                  {errores.imagenes}
                </FormHelperText>
              )
            }
            {
              !isEmpty(existingImages) && (
                <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                  {existingImages.map((img, i) => (
                    <Box key={i} sx={{ position: 'relative' }}>
                      <img
                        src={`${urlImg}/servicios/${id}/${img.nombre}`}
                        alt="Servicio"
                        style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 4 }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteImage(img.nombre)}
                        sx={{ position: 'absolute', top: 0, right: 0, bgcolor: 'rgba(255,255,255,0.7)', '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' } }}
                      >
                        <DeleteIcon fontSize="small" sx={{ color: 'error.oscuro' }} />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )
            }
          </Grid>

          <Grid size={12}>
            <Typography variant="body1" color="primary" fontWeight={600} sx={{ mt: 3 }}>
              Categorización
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="body2" color="primary" fontWeight={600} sx={{ mb: 1 }}>
                  Categoría<Box component="span" sx={{ color: 'error.oscuro' }}>*</Box>
                </Typography>
                <FormControl
                  fullWidth
                  error={!isEmpty(errores.categoria)}
                >
                  <Autocomplete
                    options={categoriaOptions}
                    getOptionLabel={(o) => o?.name || ""}
                    isOptionEqualToValue={(o, v) => o._id === v._id}
                    value={categoriaOptions.find(c => c._id === service.categoria) || null}
                    onChange={(e, nueva) => handleChange("categoria", nueva ? nueva._id : "")}
                    noOptionsText="Sin resultados"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Busca o selecciona la categoría"
                        error={!isEmpty(errores.categoria)}
                      />
                    )}
                  />
                  {
                    !isEmpty(errores.categoria) && (
                      <FormHelperText>
                        {errores?.categoria}
                      </FormHelperText>
                    )
                  }
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="body2" color="primary" fontWeight={600} sx={{ mb: 1 }}>
                  Subcategoría (Opcional)
                </Typography>
                <FormControl fullWidth>
                  <Autocomplete
                    options={subcategoriaOptions}
                    getOptionLabel={(o) => o?.name || ""}
                    isOptionEqualToValue={(o, v) => o._id === v._id}
                    value={subcategoriaOptions.find(s => s._id === service.subcategoria) || null}
                    onChange={(e, nueva) => handleChange("subcategoria", nueva ? nueva._id : "")}
                    disabled={!service.categoria || subcategoriaOptions.length === 0}
                    noOptionsText="Sin subcategorías"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={service.categoria ? "Busca o selecciona una subcategoría" : "Primero elige categoría"}
                      />
                    )}
                  />
                </FormControl>
              </Grid>
            </Grid>

            {/* Aviso: la categoría elegida exige cédula profesional aprobada */}
            {(() => {
              const catSel = categories?.find(c => String(c._id) === String(service.categoria));
              if (!catSel?.requiereCedula) return null;
              return (
                <Alert severity="warning" icon={<WorkspacePremiumOutlinedIcon />}
                  sx={{ mt: 2, borderRadius: 2, fontWeight: 500, alignItems: 'flex-start' }}>
                  <b>Esta categoría requiere cédula profesional.</b> Podrás crear el servicio,
                  pero <b>no se publicará ni se podrá contratar</b> hasta que subas tu cédula de
                  «{catSel.name}» y nuestro equipo la apruebe. Súbela en{' '}
                  <b>Mi perfil → Documentos y verificación</b>.
                </Alert>
              );
            })()}

            {/* Tags Section */}
            <Typography variant="body1" color="primary" fontWeight={600} sx={{ mt: 3, mb: 1 }}>
              Etiquetas / Palabras Clave
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  placeholder="Escribe una etiqueta"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                  autoComplete="off"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography color="primary" sx={{ fontWeight: 'bold' }}>#</Typography>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={handleAddTag}
                  sx={{ height: '56px', fontWeight: 600, textTransform: 'none' }}
                >
                  Agregar
                </Button>
              </Grid>
              <Grid size={12}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {service.tags && service.tags.map((tag, index) => (
                    <Chip
                      key={index}
                      label={tag}
                      onDelete={() => handleRemoveTag(tag)}
                      color="primary"
                      variant="outlined"
                      sx={{ borderRadius: '8px', fontWeight: 600 }}
                    />
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Grid>

          <Grid size={12}>
            <Typography variant="body1" color="primary" fontWeight={600} sx={{ mt: 3 }}>
              Modalidad del Servicio
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Typography variant="body2" sx={{ mb: 2 }}>
              Selecciona las modalidades disponibles<Box component="span" sx={{ color: 'error.oscuro' }}>*</Box>
            </Typography>

            <SelectorModule
              options={[
                { id: 'Presencial', title: 'Presencial', desc: 'En un sitio específico', icon: <LocationOnIcon fontSize="large" color={service.modalidades.includes('Presencial') ? 'primary' : 'inherit'} /> },
                { id: 'Remoto', title: 'Remoto', desc: 'En línea / Virtual', icon: <LaptopIcon fontSize="large" color={service.modalidades.includes('Remoto') ? 'primary' : 'inherit'} /> },
                { id: 'Domicilio', title: 'A Domicilio', desc: 'En casa del cliente', icon: <HomeIcon fontSize="large" color={service.modalidades.includes('Domicilio') ? 'primary' : 'inherit'} /> }
              ]}
              value={service.modalidades}
              onChange={(val) => handleChange('modalidades', val)}
              multiple
            />
            {errores.modalidades && (
              <FormHelperText error sx={{ mt: 1 }}>{errores.modalidades}</FormHelperText>
            )}

            {service.modalidades.includes('Presencial') && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" color="primary" fontWeight={600} sx={{ mb: 1 }}>
                  Ubicación del Servicio (Google Maps)<Box component="span" sx={{ color: 'error.oscuro' }}>*</Box>
                </Typography>
                <GooglePlacesAutocomplete
                  autocompletionRequest={{
                    componentRestrictions: {
                      country: ['mx'],
                    }
                  }}
                  selectProps={{
                    value: service.ubicacion.direccion ? { label: service.ubicacion.direccion, value: service.ubicacion.place_id } : null,
                    onChange: (place) => {
                      if (place) {
                        handleChange('ubicacion', {
                          direccion: place.label,
                          place_id: place.value.place_id
                        });
                        if (errores.ubicacion) {
                          setErrores(prev => ({ ...prev, ubicacion: null }));
                        }
                      } else {
                        handleChange('ubicacion', { direccion: '', place_id: '' });
                      }
                    },
                    placeholder: "Busca tu dirección...",
                    styles: {
                      input: (provided) => ({
                        ...provided,
                        height: 48,
                      }),
                      control: (provided) => ({
                        ...provided,
                        borderColor: errores.ubicacion ? palette.error.main : 'rgba(0, 0, 0, 0.23)',
                        '&:hover': {
                          borderColor: errores.ubicacion ? palette.error.main : 'rgba(0, 0, 0, 0.87)',
                        }
                      }),
                    },
                  }}
                />
                {errores.ubicacion && (
                  <FormHelperText error sx={{ mt: 1 }}>{errores.ubicacion}</FormHelperText>
                )}
              </Box>
            )}
          </Grid>

          <Grid size={12}>
            <Typography variant="body1" color="primary" fontWeight={600} sx={{ mt: 3 }}>
              Idiomas disponibles
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Typography variant="body2" sx={{ mb: 2 }}>
              Selecciona los idiomas en los que podrá ser impartido el servicio
            </Typography>

            <SelectorModule
              options={[
                { id: 'espanol', title: 'Español', icon: '🇪🇸' },
                { id: 'english', title: 'English', icon: '🇺🇸' },
                { id: 'francais', title: 'Français', icon: '🇫🇷' }
              ]}
              value={service.idiomas}
              onChange={(val) => handleChange('idiomas', val)}
              multiple
            />
          </Grid>
        </Grid>

        <Divider />

        <Grid container spacing={3} sx={{ mt: 3, mb: 4 }}>
          <Grid size={12}>
            <Typography variant="body1" color="primary" fontWeight={600}>
              Información Específicia
            </Typography>
            <Divider sx={{ mb: 3 }} />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="body2" color="primary" fontWeight={600} sx={{ mb: 1 }}>
              Unidad de Cobro<Box component="span" sx={{ color: 'error.oscuro' }}>*</Box>
            </Typography>
            <Grid container spacing={1}>
              {[
                { id: 'Servicio', title: 'Servicio', icon: <AttachMoneyIcon /> },
                { id: 'Hora', title: 'Hora', icon: <AccessTimeIcon /> },
                { id: 'Día', title: 'Día', icon: <CalendarTodayIcon /> }
              ].map((unit) => (
                <Grid size={4} key={unit.id}>
                  <Paper
                    elevation={0}
                    onClick={() => handleChange('tipoCosto', unit.id)}
                    sx={{
                      p: 1.5,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                      border: '1px solid',
                      borderColor: service.tipoCosto === unit.id ? 'primary.main' : 'grey.300',
                      borderRadius: '8px',
                      bgcolor: service.tipoCosto === unit.id ? ((t) => alpha(t.palette.primary.main, 0.04)) : 'transparent',
                      '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: (t) => alpha(t.palette.primary.main, 0.02)
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', color: service.tipoCosto === unit.id ? 'primary.main' : 'text.secondary' }}>
                      {unit.icon}
                    </Box>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color={service.tipoCosto === unit.id ? 'primary' : 'inherit'}
                    >
                      {unit.title}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
            {errores.tipoCosto && <FormHelperText error>{errores.tipoCosto}</FormHelperText>}
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="body2" color="primary" fontWeight={600} sx={{ mb: 1 }}>
              Costo desde (servicio):<Box component="span" sx={{ color: 'error.oscuro' }}>*</Box>
            </Typography>
            <TextField
              name="costo"
              placeholder="2"
              variant="outlined"
              fullWidth
              autoComplete="off"
              value={service.costoServicio}
              type="number"
              onChange={(e) => handleChange("costoServicio", e.target.value)}
              error={!isEmpty(errores.costoServicio)}
              helperText={errores?.costoServicio}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Box component="span" sx={{ color: 'primary.main' }}>$</Box>
                  </InputAdornment>
                ),
              }}
            />
            {/* Desglose de la comisión: que el proveedor fije su precio sabiendo su neto */}
            {parseFloat(service.costoServicio) > 0 && (() => {
              const costo = parseFloat(service.costoServicio);
              const comision = Math.round(costo * 10) / 100;
              const neto = Math.round((costo - comision) * 100) / 100;
              const unidad = service.tipoCosto ? ` por ${service.tipoCosto.toLowerCase()}` : '';
              return (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: 'success.oscuro' }}>
                  Recibirás <strong>{dinero(neto)}</strong>{unidad} —
                  la plataforma retiene el 10% de comisión ({dinero(comision)}).
                </Typography>
              );
            })()}
          </Grid>

          <Grid size={12}>
            <Divider sx={{ my: 1, opacity: 0.5 }} />
          </Grid>

          <Grid size={12}>
            <Typography variant="body2" color="primary" fontWeight={600} sx={{ mb: 1 }}>
              Duración Estimada <Box component="span" sx={{ fontWeight: 400, color: 'text.secondary' }}>(opcional)</Box>
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 8, md: 4 }}>
                <TextField
                  placeholder="Ej: 2"
                  fullWidth
                  value={service.duracionEstimada}
                  onChange={(e) => handleChange("duracionEstimada", e.target.value)}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <AccessTimeIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 4, md: 2 }}>
                <Select
                  fullWidth
                  value={service.unidadDuracion}
                  onChange={(e) => handleChange("unidadDuracion", e.target.value)}
                >
                  <MenuItem value="Horas">Horas</MenuItem>
                  <MenuItem value="Días">Días</MenuItem>
                  <MenuItem value="Semanas">Semanas</MenuItem>
                </Select>
              </Grid>
            </Grid>
            <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
              Indica cuánto tiempo toma completar el servicio (si aplica)
            </Typography>
          </Grid>
        </Grid>

        <Divider />

        <Typography variant="body1" color="primary" fontWeight={600} sx={{ mt: 3, mb: 1 }}>
          Disponibilidad
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <DisponibilidadSemanal
          value={service.disponibilidad}
          onChange={(disponibilidad) => {
            handleChange('disponibilidad', disponibilidad);
            if (errores.disponibilidad) setErrores(prev => ({ ...prev, disponibilidad: null }));
          }}
          error={errores.disponibilidad}
          onAviso={(message, severity) => setAlert({ open: true, message, severity })}
        />

        <Divider />

        {/* Al publicar: si todavía no puede recibir sus pagos (sin botón, para no salir del formulario) */}
        <AvisoCobro enFormulario sx={{ mt: 3 }} />

        <Grid container spacing={2} mt={3} justifyContent="flex-end">
          <Button
            variant="outlined"
            onClick={e => handleSubmit(e, true)}
            type="button"
            disabled={loading}
          >
            Guardar como borrador
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => irA("/user/servicios")}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {loading ? "Procesando..." : "Publicar servicio"}
          </Button>
        </Grid>
      </form>

      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={() => setAlert({ ...alert, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setAlert({ ...alert, open: false })} severity={alert.severity} sx={{ width: '100%' }}>
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

FormServicio.propTypes = {
  irA: PropTypes.func.isRequired,
}

export default FormServicio;
